import { isBenignSignalRAbort } from "@/domains/offline/chat/benignSignalRAbort";
import { CHAT_HUB_URL, SIGNALR_ACCESS_TOKEN_QUERY, getSignalRAccessToken } from "@/domains/shared/services/api";
import { HubConnectionBuilder, HubConnectionState, type HubConnection } from "@microsoft/signalr";

let sharedConnection: HubConnection | null = null;
let refCount = 0;
let startPromise: Promise<void> | null = null;
let stopPromise: Promise<void> | null = null;

const releasedBeforeConnectMessage = "Chat hub released before connect completed.";

const eventHandlers = new Map<string, Set<ChatHubEventHandler>>();
const boundOnConnection = new WeakMap<HubConnection, Set<string>>();
const wrappedHandlers = new WeakMap<ChatHubEventHandler, ChatHubEventHandler>();

export type ChatHubEventHandler = (...args: unknown[]) => void;

function boundKey(eventName: string, handler: ChatHubEventHandler): string {
  return `${eventName}\0${handler}`;
}

function warnChatHubConnectFailed(err: unknown): void {
  if (import.meta.env.DEV) {
    const message = err instanceof Error ? err.message : String(err);
    if (message === releasedBeforeConnectMessage) {
      return;
    }
    console.warn("[chatHub] connect failed", err);
  }
}

function warnChatHubSubscribeFailed(eventName: string, err: unknown): void {
  if (import.meta.env.DEV) {
    const message = err instanceof Error ? err.message : String(err);
    if (message === releasedBeforeConnectMessage) {
      return;
    }
    console.warn(`[chatHub] subscribe failed for ${eventName}`, err);
  }
}

function logHandlerBound(eventName: string): void {
  if (import.meta.env.DEV) {
    const count = eventHandlers.get(eventName)?.size ?? 0;
    console.info(`[chatHub] bound ${eventName} (${count} handler(s))`);
  }
}

function getOrCreateHandlerSet(eventName: string): Set<ChatHubEventHandler> {
  let handlers = eventHandlers.get(eventName);
  if (!handlers) {
    handlers = new Set();
    eventHandlers.set(eventName, handlers);
  }
  return handlers;
}

function resolveWrappedHandler(eventName: string, handler: ChatHubEventHandler): ChatHubEventHandler {
  let wrapped = wrappedHandlers.get(handler);
  if (wrapped) {
    return wrapped;
  }

  wrapped = (...args: unknown[]) => {
    if (import.meta.env.DEV) {
      console.info(`[chatHub] received ${eventName}`, args);
    }
    handler(...args);
  };
  wrappedHandlers.set(handler, wrapped);
  return wrapped;
}

function ensureHandlerBound(conn: HubConnection, eventName: string, handler: ChatHubEventHandler): void {
  const key = boundKey(eventName, handler);
  let bound = boundOnConnection.get(conn);
  if (!bound) {
    bound = new Set();
    boundOnConnection.set(conn, bound);
  }
  if (bound.has(key)) {
    return;
  }
  conn.on(eventName, resolveWrappedHandler(eventName, handler));
  bound.add(key);
  logHandlerBound(eventName);
}

function ensureAllHandlersBound(conn: HubConnection): void {
  for (const [eventName, handlers] of eventHandlers) {
    for (const handler of handlers) {
      ensureHandlerBound(conn, eventName, handler);
    }
  }
}

function unbindHandler(conn: HubConnection, eventName: string, handler: ChatHubEventHandler): void {
  const wrapped = wrappedHandlers.get(handler) ?? handler;
  conn.off(eventName, wrapped);
  boundOnConnection.get(conn)?.delete(boundKey(eventName, handler));
}

function attachConnectionLifecycle(conn: HubConnection): void {
  conn.onreconnected(() => {
    if (import.meta.env.DEV) {
      console.info("[chatHub] reconnected; re-binding handlers");
    }
    boundOnConnection.delete(conn);
    ensureAllHandlersBound(conn);
  });

  conn.onclose((error) => {
    if (import.meta.env.DEV && refCount > 0) {
      console.warn("[chatHub] connection closed while subscribers active", {
        refCount,
        error: error?.message ?? error,
      });
    }
  });
}

function buildConnection(): HubConnection {
  const conn = new HubConnectionBuilder()
    .withUrl(CHAT_HUB_URL, {
      withCredentials: true,
      ...(SIGNALR_ACCESS_TOKEN_QUERY ? { accessTokenFactory: getSignalRAccessToken } : {}),
    })
    .withAutomaticReconnect()
    .build();

  attachConnectionLifecycle(conn);
  return conn;
}

async function waitForPendingStop(): Promise<void> {
  if (stopPromise) {
    await stopPromise;
  }
}

async function stopSharedConnection(): Promise<void> {
  if (!sharedConnection) {
    return;
  }

  const conn = sharedConnection;
  sharedConnection = null;
  startPromise = null;

  stopPromise = conn.stop().catch(() => {}).finally(() => {
    stopPromise = null;
  });
  await stopPromise;
}

function scheduleStopIfIdle(): void {
  if (refCount > 0 || !sharedConnection) {
    return;
  }

  if (startPromise) {
    void startPromise.finally(() => {
      if (refCount === 0 && sharedConnection) {
        void stopSharedConnection();
      }
    });
    return;
  }

  void stopSharedConnection();
}

function dropSharedIfIdle(conn: HubConnection): void {
  if (refCount === 0 && sharedConnection === conn) {
    sharedConnection = null;
  }
}

async function startAndBind(conn: HubConnection): Promise<void> {
  await conn.start();
  ensureAllHandlersBound(conn);
  if (refCount === 0) {
    await stopSharedConnection();
  }
}

async function startShared(conn: HubConnection): Promise<void> {
  try {
    await startAndBind(conn);
  } catch (err) {
    if (refCount > 0 && sharedConnection === conn && isBenignSignalRAbort(err)) {
      sharedConnection = null;
      const next = buildConnection();
      sharedConnection = next;
      try {
        await startAndBind(next);
        return;
      } catch (retryErr) {
        dropSharedIfIdle(next);
        warnChatHubConnectFailed(retryErr);
        return;
      }
    }

    dropSharedIfIdle(conn);
    warnChatHubConnectFailed(err);
  }
}

export async function acquireChatHub(): Promise<HubConnection> {
  await waitForPendingStop();

  refCount += 1;

  if (!sharedConnection) {
    sharedConnection = buildConnection();
  }

  const conn = sharedConnection;

  if (conn.state === HubConnectionState.Connected) {
    ensureAllHandlersBound(conn);
    return conn;
  }

  if (!startPromise) {
    startPromise = startShared(conn).finally(() => {
      startPromise = null;
    });
  }

  await startPromise;

  if (!sharedConnection || sharedConnection.state !== HubConnectionState.Connected) {
    throw new Error(releasedBeforeConnectMessage);
  }

  ensureAllHandlersBound(sharedConnection);
  return sharedConnection;
}

export function releaseChatHub(): void {
  refCount = Math.max(0, refCount - 1);
  scheduleStopIfIdle();
}

export function getChatHubIfConnected(): HubConnection | null {
  if (sharedConnection?.state === HubConnectionState.Connected) {
    return sharedConnection;
  }

  return null;
}

export async function ensureChatHubStarted(): Promise<HubConnection> {
  return acquireChatHub();
}

export function subscribeChatHubEvent(eventName: string, handler: ChatHubEventHandler): () => void {
  const handlers = getOrCreateHandlerSet(eventName);
  handlers.add(handler);

  let active = true;
  let holdReleased = false;

  const existingConn = sharedConnection;
  if (existingConn?.state === HubConnectionState.Connected) {
    ensureHandlerBound(existingConn, eventName, handler);
  }

  void acquireChatHub()
    .then((conn) => {
      if (!active) {
        handlers.delete(handler);
        if (!holdReleased) {
          releaseChatHub();
          holdReleased = true;
        }
        return;
      }
      ensureHandlerBound(conn, eventName, handler);
    })
    .catch((err) => {
      warnChatHubSubscribeFailed(eventName, err);
      handlers.delete(handler);
      if (!holdReleased) {
        releaseChatHub();
        holdReleased = true;
      }
    });

  return () => {
    active = false;
    handlers.delete(handler);
    const conn = sharedConnection;
    if (conn) {
      unbindHandler(conn, eventName, handler);
    }
    if (!holdReleased) {
      releaseChatHub();
      holdReleased = true;
    }
  };
}

export async function withChatHub<T>(fn: (conn: HubConnection) => Promise<T>): Promise<T> {
  const conn = await acquireChatHub();
  try {
    return await fn(conn);
  } finally {
    releaseChatHub();
  }
}

/** @internal Test-only reset */
export function resetChatHubManagerForTests(): void {
  if (sharedConnection) {
    void sharedConnection.stop().catch(() => {});
  }
  sharedConnection = null;
  refCount = 0;
  startPromise = null;
  stopPromise = null;
  eventHandlers.clear();
}
